import { useEffect, useState } from 'react';
import { Save, RefreshCw, AlertTriangle, Database, Info } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../components/ui/ToastSystem';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

interface PlanCount {
  tier: string;
  count: number;
}

interface DbConfig {
  mau_limit: number;
}

const DEFAULT_DB_CONFIG: DbConfig = { mau_limit: 10_000 };

function MetricCard({
  label, value, color, sub,
}: { label: string; value: string; color: string; sub?: string }) {
  return (
    <div className="card" style={{ textAlign: 'center' }}>
      <p style={{ fontSize: '1.5rem', fontWeight: 700, color, margin: '0 0 0.25rem' }}>{value}</p>
      {sub && <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '0 0 0.25rem' }}>{sub}</p>}
      <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>{label}</p>
    </div>
  );
}

export function AdminDatabaseMonitoringPage() {
  const { showToast } = useToast();

  const [loading,      setLoading]      = useState(true);
  const [mau,          setMau]          = useState(0);
  const [totalUsers,   setTotalUsers]   = useState(0);
  const [planCounts,   setPlanCounts]   = useState<PlanCount[]>([]);
  const [dbConfig,     setDbConfig]     = useState<DbConfig>(DEFAULT_DB_CONFIG);
  const [savingConfig, setSavingConfig] = useState(false);
  const [mauLimitInput, setMauLimitInput] = useState('10000');

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);

    const thirtyDaysAgo = new Date(Date.now() - 30 * 86_400_000).toISOString();

    const [mauRes, totalRes, plansRes, configRes] = await Promise.all([
      supabase
        .from('profiles')
        .select('id', { count: 'exact', head: true })
        .gte('updated_at', thirtyDaysAgo),
      supabase
        .from('profiles')
        .select('id', { count: 'exact', head: true }),
      supabase
        .from('profiles')
        .select('subscription_tier'),
      supabase
        .from('platform_settings')
        .select('value')
        .eq('key', 'db_config')
        .maybeSingle(),
    ]);

    setMau(mauRes.count ?? 0);
    setTotalUsers(totalRes.count ?? 0);

    // Aggregate plan counts
    if (plansRes.data) {
      const map: Record<string, number> = {};
      for (const row of plansRes.data) {
        const tier = (row.subscription_tier as string | null) ?? 'free';
        map[tier] = (map[tier] ?? 0) + 1;
      }
      const list = Object.entries(map)
        .map(([tier, count]) => ({ tier, count }))
        .sort((a, b) => b.count - a.count);
      setPlanCounts(list);
    }

    if (configRes.data?.value) {
      try {
        const parsed = typeof configRes.data.value === 'string'
          ? JSON.parse(configRes.data.value)
          : configRes.data.value;
        if (parsed && typeof parsed.mau_limit === 'number') {
          setDbConfig(parsed as DbConfig);
          setMauLimitInput(String(parsed.mau_limit));
        }
      } catch { /* use default */ }
    }

    setLoading(false);
  }

  async function saveConfig() {
    const limit = parseInt(mauLimitInput) || DEFAULT_DB_CONFIG.mau_limit;
    const newConfig: DbConfig = { mau_limit: limit };
    setSavingConfig(true);
    const { error } = await supabase.from('platform_settings').upsert({
      key: 'db_config',
      value: JSON.stringify(newConfig),
      description: 'Configuração de limites do banco de dados',
      updated_at: new Date().toISOString(),
    }, { onConflict: 'key' });
    setSavingConfig(false);
    if (error) showToast({ type: 'error', message: 'Erro ao salvar configuração' });
    else {
      setDbConfig(newConfig);
      showToast({ type: 'success', message: 'Configuração salva!' });
    }
  }

  const mauPct = dbConfig.mau_limit > 0 ? Math.round((mau / dbConfig.mau_limit) * 100) : 0;
  const isNearLimit = mauPct >= 80;

  function tierLabel(tier: string): string {
    const labels: Record<string, string> = {
      free: 'Free', monthly: 'Mensal', annual: 'Anual', pro: 'Pro',
    };
    return labels[tier] ?? tier;
  }

  return (
    <div>
      <AdminPageHeader
        title="Monitoramento do Banco de Dados"
        objective="Visualize métricas de usuários, distribuição por plano e limites de MAU. Queries lentas e uso de storage devem ser monitorados diretamente no Supabase Dashboard."
      />

      {/* MAU Alert Banner */}
      {!loading && isNearLimit && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem',
          padding: '0.75rem 1rem',
          backgroundColor: 'rgba(251,191,36,0.1)',
          border: '1px solid rgba(251,191,36,0.35)',
          borderRadius: 'var(--radius-sm)',
          marginBottom: '1.5rem',
          color: '#fbbf24',
          fontSize: 'var(--text-caption)',
        }}>
          <AlertTriangle size={16} style={{ flexShrink: 0 }} />
          <span>
            <strong>Atenção:</strong> O MAU atual ({mau.toLocaleString('pt-BR')}) representa {mauPct}% do limite configurado ({dbConfig.mau_limit.toLocaleString('pt-BR')}). Considere aumentar o limite ou fazer upgrade no Supabase.
          </span>
        </div>
      )}

      {/* Top metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        <MetricCard
          label="MAU (últ. 30 dias)"
          value={mau.toLocaleString('pt-BR')}
          color={isNearLimit ? '#f87171' : '#4ade80'}
          sub={`${mauPct}% do limite`}
        />
        <MetricCard
          label="Total de usuários"
          value={totalUsers.toLocaleString('pt-BR')}
          color="#60a5fa"
        />
      </div>

      {loading ? (
        <div className="card shimmer" style={{ height: '300px' }} />
      ) : (
        <>
          {/* Plan distribution */}
          <div style={{ marginBottom: '2rem' }}>
            <p style={{ fontSize: 'var(--text-caption)', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.75rem' }}>
              Distribuição por Plano
            </p>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Plano</th>
                    <th style={{ textAlign: 'right' }}>Usuários</th>
                    <th style={{ textAlign: 'right' }}>% do total</th>
                  </tr>
                </thead>
                <tbody>
                  {planCounts.length === 0 ? (
                    <tr>
                      <td colSpan={3} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: '2rem' }}>
                        Nenhum dado de plano encontrado.
                      </td>
                    </tr>
                  ) : (
                    planCounts.map(p => (
                      <tr key={p.tier}>
                        <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                          <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', background: 'rgba(31,78,95,0.1)', color: 'var(--petrol)' }}>
                            {tierLabel(p.tier)}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--text-secondary)' }}>{p.count.toLocaleString('pt-BR')}</td>
                        <td style={{ textAlign: 'right', color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
                          {totalUsers > 0 ? `${Math.round((p.count / totalUsers) * 100)}%` : '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Storage & Slow Queries, informational */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
            <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <p style={{ fontWeight: 700, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <Database size={14} /> Uso de Storage
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>
                O uso de storage não pode ser consultado diretamente via API. Acesse o Supabase Dashboard → Storage para ver os dados atualizados.
              </p>
              <a
                href="https://app.supabase.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary btn-sm"
                style={{ alignSelf: 'flex-start', fontSize: '11px' }}
              >
                Abrir Supabase Dashboard ↗
              </a>
            </div>

            <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <p style={{ fontWeight: 700, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <Info size={14} /> Queries Lentas
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>
                Monitore queries lentas no Supabase Dashboard → Database → Query Performance para identificar gargalos de performance.
              </p>
              <a
                href="https://app.supabase.com"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary btn-sm"
                style={{ alignSelf: 'flex-start', fontSize: '11px' }}
              >
                Abrir Query Performance ↗
              </a>
            </div>
          </div>

          {/* DB Config, MAU limit */}
          <div style={{ marginBottom: '2rem' }}>
            <p style={{ fontSize: 'var(--text-caption)', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.75rem' }}>
              Configuração de Limites
            </p>
            <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', display: 'block', marginBottom: '0.3rem' }}>
                    Limite de MAU (alerta em 80%)
                  </label>
                  <input
                    type="number"
                    className="form-input"
                    value={mauLimitInput}
                    min={100}
                    step={1000}
                    onChange={e => setMauLimitInput(e.target.value)}
                  />
                </div>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={saveConfig}
                  disabled={savingConfig}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginBottom: '0.05rem' }}
                >
                  <Save size={13} /> {savingConfig ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
              <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>
                Um aviso aparecerá nesta página quando o MAU ultrapassar 80% deste limite.
              </p>
            </div>
          </div>

          {/* Reload */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost btn-sm" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <RefreshCw size={13} /> Recarregar dados
            </button>
          </div>
        </>
      )}
    </div>
  );
}
